import '../styles/LazyLoad.css';

import React, { useState, useContext, useEffect } from 'react';
import { FaFemale, FaMale, FaTheaterMasks, FaGraduationCap, FaVideo, FaBuilding, FaHistory, FaTimes, FaChevronLeft, FaChevronRight, FaSpinner } from 'react-icons/fa';
import { educationImages } from '../data/educationImages';
import '../styles/EducationGallery.css';
import '../media-query/edugallery.css';
import { LanguageContext } from '../context/LanguageContext';

const EducationGallery = () => {
  const [activeCategory, setActiveCategory] = useState('oldSchool');
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showSkeleton, setShowSkeleton] = useState(true);
  const { translations } = useContext(LanguageContext);

  const t = translations.EducationGallery;

  const galleryData = {
    oldSchool: {
      title: t.galleryTitles?.oldSchool || 'Old School',
      icon: <FaHistory />,
      images: educationImages.oldSchool?.images || [],
    },
    newSchool: {
      title: t.galleryTitles?.newSchool || 'New School',
      icon: <FaBuilding />,
      images: educationImages.newSchool?.images || [],
    },
    girls: {
      title: t.galleryTitles?.girls || 'Girls Education',
      icon: <FaFemale />,
      images: educationImages.girls?.images || [],
    },
    boys: {
      title: t.galleryTitles?.boys || 'Boys Education',
      icon: <FaMale />,
      images: educationImages.boys?.images || [],
    },
    other: {
      title: t.galleryTitles?.other || 'Other Activities',
      icon: <FaTheaterMasks />,
      images: educationImages.other?.images || [],
    },
    videos: {
      title: t.galleryTitles?.videos || 'Education Videos',
      icon: <FaVideo />,
      images: [],
      videos: [],
    },
  };

  const categories = [
    { key: 'oldSchool', icon: <FaHistory /> },
    { key: 'newSchool', icon: <FaBuilding /> },
    { key: 'girls', icon: <FaFemale /> },
    { key: 'boys', icon: <FaMale /> },
    { key: 'other', icon: <FaTheaterMasks /> },
    { key: 'videos', icon: <FaVideo /> },
  ];

  const currentContent = galleryData[activeCategory];

  // Show skeleton only briefly, then show images
  useEffect(() => {
    setShowSkeleton(true);
    const timer = setTimeout(() => {
      setShowSkeleton(false);
    }, 500);
    
    return () => clearTimeout(timer);
  }, [activeCategory]);

  const openLightbox = (image, index) => {
    setSelectedImage(image);
    setCurrentIndex(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const goToPrev = () => {
    const images = currentContent.images;
    if (!images || images.length === 0) return;
    const newIndex = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
    setSelectedImage(images[newIndex]);
  };

  const goToNext = () => {
    const images = currentContent.images;
    if (!images || images.length === 0) return;
    const newIndex = currentIndex === images.length - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
    setSelectedImage(images[newIndex]);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') closeLightbox();
    if (e.key === 'ArrowLeft') goToPrev();
    if (e.key === 'ArrowRight') goToNext();
  };

  useEffect(() => {
    if (selectedImage) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [selectedImage, currentIndex]);

  const getCategoryLabel = (key) => {
    if (t.categories && t.categories[key]) {
      return t.categories[key];
    }
    const labels = {
      'oldSchool': 'Old School',
      'newSchool': 'New School',
      'girls': 'Girls Education',
      'boys': 'Boys Education',
      'other': 'Other Activities',
      'videos': 'Education Videos'
    };
    return labels[key] || key;
  };

  // Skeleton loader component
  const SkeletonLoader = ({ count = 6 }) => {
    return (
      <div className="education-images-grid">
        {Array(count).fill(0).map((_, index) => (
          <div key={`skeleton-${index}`} className="education-image-card skeleton-card">
            <div className="skeleton-image"></div>
            <div className="skeleton-shimmer"></div>
          </div>
        ))}
      </div>
    );
  };

  // Image component - shows immediately with placeholder
  const GalleryImage = ({ src, alt, onClick }) => {
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(false);

    return (
      <div className="education-image-card" onClick={onClick}>
        {!loaded && !error && (
          <div className="image-placeholder">
            <FaSpinner className="spinner" />
          </div>
        )}
        {error && (
          <div className="image-error">
            <FaGraduationCap />
            <span>Failed to load</span>
          </div>
        )}
        <img 
          src={src} 
          alt={alt}
          className="education-gallery-image"
          onLoad={() => setLoaded(true)}
          onError={() => setError(true)}
          loading="lazy"
        />
      </div>
    );
  };

  return (
    <div className="education-gallery">
      {/* Header */}
      <div className="education-header">
        <div className="education-header-left">
          <div className="education-header-icon">
            <FaGraduationCap />
          </div>
          <h1 className="education-header-title">{t.headerTitle || 'Education Gallery'}</h1>
        </div>
      </div>

      <div className="education-container">
        {/* Sidebar */}
        <div className="education-sidebar">
          <h3 className="education-sidebar-title">{t.sidebarTitle || 'Categories'}</h3>
          <ul className="education-category-list">
            {categories.map((category) => (
              <li
                key={category.key}
                className={`education-category-item ${activeCategory === category.key ? 'active' : ''}`}
                onClick={() => setActiveCategory(category.key)}
              >
                <span className="education-category-icon">{category.icon}</span>
                <span className="education-category-label">{getCategoryLabel(category.key)}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Gallery */}
        <div className="education-content">
          <div className="education-content-header">
            <div className="education-content-title">
              <span className="education-title-icon">{currentContent.icon}</span>
              <h2>{currentContent.title}</h2>
              <span className="image-count">
                ({currentContent.images?.length || 0} {t.images || 'images'})
              </span>
            </div>
          </div>

          {activeCategory === 'videos' ? (
            currentContent.videos && currentContent.videos.length > 0 ? (
              <div className="education-videos-grid">
                {currentContent.videos.map((video) => (
                  <div key={video.id} className="education-video-card">
                    <div className="video-container">
                      <iframe
                        src={video.src}
                        title={video.title}
                        className="education-gallery-video"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        loading="lazy"
                      ></iframe>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-content-message">
                <div className="no-content-icon">
                  <FaVideo />
                </div>
                <p>{t.noVideos || 'No videos available'}</p>
              </div>
            )
          ) : (
            showSkeleton ? (
              <SkeletonLoader count={Math.min(currentContent.images?.length || 6, 6)} />
            ) : currentContent.images && currentContent.images.length > 0 ? (
              <div className="education-images-grid">
                {currentContent.images.map((item, index) => (
                  <GalleryImage
                    key={item.id || index}
                    src={item.src}
                    alt={`${currentContent.title} - ${t.image || 'Image'} ${index + 1}`}
                    onClick={() => openLightbox(item, index)}
                  />
                ))}
              </div>
            ) : (
              <div className="no-content-message">
                <div className="no-content-icon">
                  {currentContent.icon}
                </div>
                <p>{t.noImages || 'No images available'}</p>
              </div>
            )
          )}
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="close-lightbox" 
              onClick={closeLightbox}
              title={t.close || 'Close'}
              aria-label={t.close || 'Close'}
            >
              <FaTimes />
            </button>
            
            <img 
              src={selectedImage.src} 
              alt={t.fullView || 'Full view'} 
              className="lightbox-image"
              loading="lazy"
            />
            
            {currentContent.images && currentContent.images.length > 1 && (
              <div className="lightbox-nav">
                <button 
                  className="nav-btn" 
                  onClick={goToPrev}
                  title={t.prev || 'Previous'}
                  aria-label={t.prev || 'Previous'}
                >
                  <FaChevronLeft />
                </button>
                <button 
                  className="nav-btn" 
                  onClick={goToNext}
                  title={t.next || 'Next'}
                  aria-label={t.next || 'Next'}
                >
                  <FaChevronRight />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EducationGallery;